import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/content";
import { MaskedLines } from "./Reveal";

const ZODIAC =
  "https://images.unsplash.com/photo-1729335511883-29eade10006b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhc3Ryb2xvZ3klMjB6b2RpYWMlMjB2ZWRpYyUyMGNoYXJ0fGVufDB8fHx8MTc4NjcxMTU0Nnww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 24]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24"
    >
      <Constellation />
      {/* duotone filter: maps black -> forest green, white -> warm cream */}
      <svg aria-hidden="true" width="0" height="0" className="absolute">
        <filter id="duo-forest">
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="0.13 0.96" />
            <feFuncG type="table" tableValues="0.35 0.94" />
            <feFuncB type="table" tableValues="0.27 0.89" />
          </feComponentTransfer>
        </filter>
      </svg>
      <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-6">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="overline mb-7"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="heading-serif text-forest text-5xl leading-[0.92] tracking-tight sm:text-6xl md:text-7xl lg:text-[6.5rem]">
            <MaskedLines lines={t.hero.titleLines} delay={0.35} trigger="load" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="body-text mt-9 max-w-xl text-base md:text-lg"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#services" data-testid="hero-cta-services" className="btn-primary">
              {t.hero.ctaPrimary}
              <ArrowUpRight size={18} strokeWidth={1.75} />
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="btn-ghost"
            >
              <MessageCircle size={18} strokeWidth={1.75} />
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="relative mx-auto aspect-[3/4] w-full max-w-sm"
          >
            <motion.div
              style={{ y: imgY }}
              className="hero-arch h-full w-full overflow-hidden"
            >
              <img
                src={ZODIAC}
                alt="Vedic zodiac chart"
                className="duo-forest-img h-full w-full object-cover"
              />
            </motion.div>

            {/* rotating seal */}
            <motion.div
              style={{ rotate: imgRotate }}
              className="absolute -left-6 -top-6 h-24 w-24 md:-left-10 md:h-28 md:w-28"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="h-full w-full"
              >
                <SealText />
              </motion.div>
              <span className="absolute inset-0 flex items-center justify-center text-2xl text-terracotta">
                ॐ
              </span>
            </motion.div>

            {/* badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -bottom-5 -right-3 rounded-2xl border border-line bg-bg px-5 py-4 shadow-sm md:-right-8"
              data-testid="hero-experience-badge"
            >
              <span className="heading-serif block text-4xl leading-none text-terracotta">
                15+
              </span>
              <span className="mt-1 block text-xs tracking-wide text-ink-soft">
                {lang === "en" ? "Years of practice" : lang === "hi" ? "वर्षों का अनुभव" : "Saal ka anubhav"}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SealText = () => (
  <svg viewBox="0 0 100 100" className="h-full w-full">
    <defs>
      <path id="circlePath" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
    </defs>
    <text className="seal-text" fill="#B8912A">
      <textPath href="#circlePath" startOffset="0%">
        · VASTU · JYOTISH · NUMEROLOGY · HEALING
      </textPath>
    </text>
  </svg>
);


const STARS = [
  { x: 60, y: 40, r: 2.2 }, { x: 150, y: 90, r: 1.4 }, { x: 240, y: 55, r: 2.6 },
  { x: 330, y: 130, r: 1.6 }, { x: 120, y: 200, r: 1.8 }, { x: 280, y: 210, r: 1.3 },
  { x: 380, y: 260, r: 2.3 }, { x: 200, y: 300, r: 1.5 }, { x: 90, y: 330, r: 2.0 },
  { x: 320, y: 360, r: 1.4 }, { x: 420, y: 160, r: 1.7 }, { x: 40, y: 130, r: 1.3 },
];

const LINES = [
  [0, 2], [2, 3], [3, 6], [6, 9], [4, 8], [1, 4], [2, 5], [5, 6], [7, 9], [0, 11],
];

const Constellation = () => (
  <div className="constellation">
    <svg viewBox="0 0 460 400" fill="none" xmlns="http://www.w3.org/2000/svg">
      {LINES.map(([a, b], i) => (
        <line
          key={`l-${i}`}
          x1={STARS[a].x} y1={STARS[a].y}
          x2={STARS[b].x} y2={STARS[b].y}
          stroke="#B8912A" strokeWidth="0.6" strokeOpacity="0.35"
        />
      ))}
      {STARS.map((s, i) => (
        <circle
          key={`s-${i}`}
          className="twinkle"
          cx={s.x} cy={s.y} r={s.r}
          fill={i % 3 === 0 ? "#B8912A" : "#1F5A44"}
          style={{ animationDelay: `${(i % 6) * 0.5}s` }}
        />
      ))}
    </svg>
  </div>
);
